import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

export interface PlanningLot {
  id: string;
  org_id: string;
  code: string;
  client_id: string | null;
  varietal: string | null;
  vintage: number | null;
  current_volume_l: number | null;
  notes: string | null;
  wine_lot_id: string | null;
  is_received: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlanningLotWithClient extends PlanningLot {
  client: { id: string; name: string } | null;
}

export interface CreatePlanningLotInput {
  code: string;
  client_id?: string | null;
  varietal?: string | null;
  vintage?: number | null;
  current_volume_l?: number | null;
  notes?: string | null;
}

export interface MarkReceivedInput {
  current_volume_l?: number | null;
  wine_lot_id?: string | null;
}

// ============================================================================
// Lot code parsing
// ============================================================================

export interface ParsedLotCode {
  cc: string;          // 2-char client segment
  yy: string;          // 2-char year segment
  vv: string;          // 2-char varietal segment
  identifier: string;  // remainder
  vintageYear: number | null;
}

/**
 * Parse a lot code following the CCYYVV[identifier] convention.
 * Requires at least 6 characters (CC + YY + VV).
 * Returns null if code is too short.
 */
export function parseLotCode(code: string): ParsedLotCode | null {
  const normalized = code.toUpperCase().replace(/\s/g, "");
  if (normalized.length < 6) return null;

  const cc = normalized.slice(0, 2);
  const yy = normalized.slice(2, 4);
  const vv = normalized.slice(4, 6);
  const identifier = normalized.slice(6);

  const yyInt = parseInt(yy, 10);
  const vintageYear = isNaN(yyInt) ? null : 2000 + yyInt;

  return { cc, yy, vv, identifier, vintageYear };
}

// ============================================================================
// Query: look up a planning_lot by exact code (used in PlanLotDialog)
// ============================================================================

export function usePlanningLotByCode(code: string) {
  const normalized = code.toUpperCase().replace(/\s/g, "");
  return useQuery({
    queryKey: ["planning-lot-by-code", normalized],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("planning_lot")
        .select("*, client:client_id(id, name)")
        .eq("code", normalized)
        .maybeSingle();
      if (error) throw error;
      return data as PlanningLotWithClient | null;
    },
    enabled: normalized.length >= 6,
    staleTime: 10000,
  });
}

// ============================================================================
// Mutation: Create planning lot
// ============================================================================

export function useCreatePlanningLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePlanningLotInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("planning_lot")
        .insert([{
          ...input,
          code: input.code.toUpperCase().replace(/\s/g, ""),
          org_id: userData.org_id,
          is_received: false,
        }])
        .select("*, client:client_id(id, name)")
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error(`A planning lot with code "${input.code.toUpperCase()}" already exists`);
        }
        throw error;
      }
      return data as PlanningLotWithClient;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["planning-lot-by-code"] });
      queryClient.invalidateQueries({ queryKey: ["planning-lots"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ============================================================================
// Mutation: Mark planning lot as received
// ============================================================================

export function useMarkPlanningLotReceived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: MarkReceivedInput }) => {
      const { data, error } = await supabase
        .from("planning_lot")
        .update({
          is_received: true,
          current_volume_l: input.current_volume_l ?? null,
          wine_lot_id: input.wine_lot_id ?? null,
        })
        .eq("id", id)
        .select("*, client:client_id(id, name)")
        .single();

      if (error) throw error;
      return data as PlanningLotWithClient;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["planning-lot-by-code", data.code] });
      toast.success(`${data.code} marked as received`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to mark as received: ${error.message}`);
    },
  });
}
